// Authentication store for Bonapisca
import React from 'react';
import i18next from 'i18next';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from 'aws-amplify/auth';
import type { SupportedLocale } from '../types';
import { analyticsService } from '../services/analytics';
import { PUBLIC_ACCOUNT_FEATURES_ENABLED } from '../config/publicFeatures';

type LoginResult = 'signed_in' | 'new_password_required';

const tAuthStore = (key: string, options?: Record<string, unknown>) =>
  String(i18next.t(`auth:storeErrors.${key}`, options));

const publicAuthDisabledError = () => new Error(tAuthStore('publicLoginDisabled'));
const loadAmplifyAuth = async () => {
  if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
    throw publicAuthDisabledError();
  }

  // Account login is disabled for launch; keep Amplify Auth out of the public bundle.
  const moduleName = 'aws-amplify/auth';
  return import(/* @vite-ignore */ moduleName);
};

export interface PendingAuthChallenge {
  type: 'new_password_required';
  email: string;
  missingAttributes: string[];
}

const clearPersistedAuthState = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem('post-login-reload');
  window.localStorage.removeItem('auth-storage');

  const legacyAuthPrefix = ['Cognito', 'IdentityServiceProvider.'].join('');
  for (const key of Object.keys(window.localStorage)) {
    if (key.startsWith(legacyAuthPrefix)) {
      window.localStorage.removeItem(key);
    }
  }
};

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  idToken: string | null;
  accessToken: string | null;
  preferredLanguage: SupportedLocale;
  experienceLevel: 'beginner' | 'intermediate' | 'advanced';
  location: string | null;
  pendingChallenge: PendingAuthChallenge | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeNewPasswordChallenge: (newPassword: string) => Promise<void>;
  clearPendingChallenge: () => void;
  logout: () => Promise<void>;
  register: (email: string, password: string, attributes?: Record<string, string>) => Promise<void>;
  confirmRegistration: (email: string, code: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmNewPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  updatePreferences: (preferences: {
    preferredLanguage?: SupportedLocale;
    experienceLevel?: 'beginner' | 'intermediate' | 'advanced';
    location?: string;
  }) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: PUBLIC_ACCOUNT_FEATURES_ENABLED,
      error: null,
      idToken: null,
      accessToken: null,
      preferredLanguage: 'en-GB',
      experienceLevel: 'beginner',
      location: null,
      pendingChallenge: null,

      login: async (email: string, password: string) => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          throw publicAuthDisabledError();
        }

        set({ isLoading: true, error: null, pendingChallenge: null });

        try {
          const { signIn } = await loadAmplifyAuth();
          const result = await signIn({
            username: email,
            password
          });

          if (result.isSignedIn || result.nextStep.signInStep === 'DONE') {
            await get().refreshSession();

            const state = get();
            analyticsService.setAuthContext({
              isAuthenticated: true,
              idToken: state.idToken,
              userId: state.user?.userId || null,
              email: state.user?.signInDetails?.loginId || null
            });
            analyticsService.trackAuthEvent('auth_login', {
              route: '/login',
              pageCategory: 'auth',
              locale: state.preferredLanguage,
              metadata: {
                authProvider: 'cognito'
              }
            });

            set({ isLoading: false, pendingChallenge: null });
            return 'signed_in';
          }

          if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            set({
              isLoading: false,
              pendingChallenge: {
                type: 'new_password_required',
                email,
                missingAttributes: result.nextStep.missingAttributes || []
              }
            });
            return 'new_password_required';
          }

          set({
            isLoading: false,
            error: tAuthStore('unsupportedSignInStep', { step: result.nextStep.signInStep })
          });
          throw new Error(tAuthStore('unsupportedSignInStep', { step: result.nextStep.signInStep }));
        } catch (error) {
          console.error('Login failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('loginFailed')
          });
          throw error;
        }
      },

      completeNewPasswordChallenge: async (newPassword: string) => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          throw publicAuthDisabledError();
        }

        const challenge = get().pendingChallenge;

        if (!challenge || challenge.type !== 'new_password_required') {
          const error = new Error(tAuthStore('noActiveNewPasswordChallenge'));
          set({ error: error.message });
          throw error;
        }

        if (challenge.missingAttributes.length > 0) {
          const error = new Error(tAuthStore('missingProfileAttributes'));
          set({ error: error.message });
          throw error;
        }

        set({ isLoading: true, error: null });

        try {
          const { confirmSignIn } = await loadAmplifyAuth();
          const result = await confirmSignIn({
            challengeResponse: newPassword
          });

          if (!result.isSignedIn && result.nextStep.signInStep !== 'DONE') {
            throw new Error(`Unexpected sign-in step after password change: ${result.nextStep.signInStep}`);
          }

          await get().refreshSession();

          const state = get();
          analyticsService.setAuthContext({
            isAuthenticated: true,
            idToken: state.idToken,
            userId: state.user?.userId || null,
            email: state.user?.signInDetails?.loginId || challenge.email
          });
          analyticsService.trackAuthEvent('auth_login', {
            route: '/force-new-password',
            pageCategory: 'auth',
            locale: state.preferredLanguage,
            metadata: {
              authProvider: 'cognito',
              challengeCompleted: 'new_password_required'
            }
          });

          set({
            isLoading: false,
            pendingChallenge: null,
            error: null
          });
        } catch (error) {
          console.error('Complete new password challenge failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('updatePasswordFailed')
          });
          throw error;
        }
      },

      clearPendingChallenge: () => set({ pendingChallenge: null }),

      logout: async () => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          clearPersistedAuthState();
          analyticsService.setAuthContext({ isAuthenticated: false, idToken: null, userId: null, email: null });
          set({
            user: null,
            isAuthenticated: false,
            idToken: null,
            accessToken: null,
            isLoading: false,
            error: null,
            pendingChallenge: null
          });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const { signOut } = await loadAmplifyAuth();
          await signOut();
        } catch (error) {
          console.error('Logout failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('logoutFailed')
          });
          throw error;
        }

        const currentState = get();
        analyticsService.trackAuthEvent('auth_logout', {
          route: '/logbook',
          pageCategory: 'logbook',
          locale: currentState.preferredLanguage,
          metadata: {
            authProvider: 'cognito'
          }
        });
        analyticsService.setAuthContext({ isAuthenticated: false, idToken: null, userId: null, email: null });

        clearPersistedAuthState();

        set({
          user: null,
          isAuthenticated: false,
          idToken: null,
          accessToken: null,
          isLoading: false,
          error: null,
          pendingChallenge: null
        });
      },

      register: async (email: string, password: string, attributes = {}) => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          throw publicAuthDisabledError();
        }

        set({ isLoading: true, error: null });

        try {
          const { signUp } = await loadAmplifyAuth();
          const result = await signUp({
            username: email,
            password,
            options: {
              userAttributes: {
                email,
                ...attributes
              }
            }
          });

          set({ isLoading: false });

          if (!result.isSignUpComplete) {
            console.log('Confirmation required for:', result.userId);
          }
        } catch (error) {
          console.error('Registration failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('registrationFailed')
          });
          throw error;
        }
      },

      confirmRegistration: async (email: string, code: string) => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          throw publicAuthDisabledError();
        }

        set({ isLoading: true, error: null });

        try {
          const { confirmSignUp } = await loadAmplifyAuth();
          await confirmSignUp({
            username: email,
            confirmationCode: code
          });

          set({ isLoading: false });
        } catch (error) {
          console.error('Confirmation failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('confirmationFailed')
          });
          throw error;
        }
      },

      resendConfirmation: async (email: string) => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          throw publicAuthDisabledError();
        }

        set({ isLoading: true, error: null });

        try {
          const { resendSignUpCode } = await loadAmplifyAuth();
          await resendSignUpCode({ username: email });
          set({ isLoading: false });
        } catch (error) {
          console.error('Resend confirmation failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('resendConfirmationFailed')
          });
          throw error;
        }
      },

      forgotPassword: async (email: string) => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          throw publicAuthDisabledError();
        }

        set({ isLoading: true, error: null });

        try {
          const { resetPassword } = await loadAmplifyAuth();
          await resetPassword({ username: email });
          set({ isLoading: false });
        } catch (error) {
          console.error('Forgot password failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('sendResetCodeFailed')
          });
          throw error;
        }
      },

      confirmNewPassword: async (email: string, code: string, newPassword: string) => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          throw publicAuthDisabledError();
        }

        set({ isLoading: true, error: null });

        try {
          const { confirmResetPassword } = await loadAmplifyAuth();
          await confirmResetPassword({
            username: email,
            confirmationCode: code,
            newPassword
          });

          set({ isLoading: false });
        } catch (error) {
          console.error('Password reset failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('passwordResetFailed')
          });
          throw error;
        }
      },

      refreshSession: async () => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          clearPersistedAuthState();
          set({
            user: null,
            isAuthenticated: false,
            idToken: null,
            accessToken: null,
            isLoading: false,
            error: null,
            pendingChallenge: null
          });
          return;
        }

        try {
          const { getCurrentUser, fetchAuthSession } = await loadAmplifyAuth();
          const [currentUser, session] = await Promise.all([
            getCurrentUser(),
            fetchAuthSession()
          ]);

          const idToken = session.tokens?.idToken?.toString() || null;
          const accessToken = session.tokens?.accessToken?.toString() || null;
          const userAttributes = currentUser.signInDetails?.loginId
            ? await getCurrentUser()
            : null;

          set({
            user: currentUser,
            isAuthenticated: true,
            idToken,
            accessToken,
            pendingChallenge: null,
            preferredLanguage: (userAttributes as any)?.['custom:preferred_language'] as SupportedLocale || get().preferredLanguage,
            experienceLevel: (userAttributes as any)?.['custom:experience_level'] as 'beginner' | 'intermediate' | 'advanced' || get().experienceLevel,
            location: (userAttributes as any)?.['custom:location'] || get().location,
          });
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            idToken: null,
            accessToken: null,
          });
        }
      },

      checkAuthStatus: async () => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          clearPersistedAuthState();
          analyticsService.setAuthContext({ isAuthenticated: false, idToken: null, userId: null, email: null });
          set({
            user: null,
            isAuthenticated: false,
            idToken: null,
            accessToken: null,
            isLoading: false,
            error: null,
            pendingChallenge: null
          });
          return;
        }

        set({ isLoading: true, error: null });

        if (import.meta.env.DEV) {
          console.log('🔐 Checking auth status...');
        }

        try {
          await get().refreshSession();

          if (import.meta.env.DEV) {
            const newState = get();
            if (newState.isAuthenticated && newState.user) {
              console.log('✅ Auth verified:', newState.user.signInDetails?.loginId || newState.user.username || newState.user.userId);
            } else {
              console.log('ℹ️ No active session');
            }
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.log('❌ Auth failed:', error instanceof Error ? error.message : tAuthStore('unknown'));
          }

          set({
            user: null,
            isAuthenticated: false,
            idToken: null,
            accessToken: null,
            pendingChallenge: null
          });
        } finally {
          set({ isLoading: false });
        }
      },

      updatePreferences: async (preferences) => {
        if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
          throw publicAuthDisabledError();
        }

        set({ isLoading: true, error: null });

        try {
          set(state => ({
            ...state,
            ...preferences,
            isLoading: false
          }));
        } catch (error) {
          console.error('Failed to update preferences:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : tAuthStore('updatePreferencesFailed')
          });
          throw error;
        }
      },

      clearError: () => set({ error: null }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        preferredLanguage: state.preferredLanguage,
        experienceLevel: state.experienceLevel,
        location: state.location,
        pendingChallenge: state.pendingChallenge,
      }),
    }
  )
);

export const useAuth = () => {
  const auth = useAuthStore();
  return {
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    isLoading: auth.isLoading,
    error: auth.error,
    pendingChallenge: auth.pendingChallenge,
    login: auth.login,
    logout: auth.logout,
    clearError: auth.clearError,
    checkAuthStatus: auth.checkAuthStatus,
    completeNewPasswordChallenge: auth.completeNewPasswordChallenge,
  };
};

export const useAuthCheck = () => {
  const { isAuthenticated, user, isLoading, checkAuthStatus } = useAuthStore();

  const verifyAuth = React.useCallback(async () => {
    if (!isLoading) {
      await checkAuthStatus();
    }
  }, [isLoading, checkAuthStatus]);

  return {
    isAuthenticated,
    user,
    isLoading,
    verifyAuth,
    isVerified: !isLoading && (isAuthenticated ? !!user : true)
  };
};
